// Cache locale del manifest uscite (IndexedDB) — stesso pattern di writeQueue.ts. Conserva
// l'ultimo snapshot noto (recordId+modId annidati, niente dati pieni) per poterlo confrontare
// con un fetch fresco via diffManifestTree e sapere cosa è cambiato senza dover riscaricare tutto.
//
// Questo modulo copre solo discovery+diff (pezzi 1+5+6 del piano in CLAUDE.md "Cosa servirebbe
// per il pattern manifest"). Il fetch mirato dei recordId add/changed (pezzo 3, dati pieni per
// record) e l'uso pratico lato UI restano da costruire — vedi CLAUDE.md per lo stato del piano.
import { openDB, IDBPDatabase } from 'idb';
import { diffManifestTree, ManifestEntry, ManifestDiffPlan } from './manifestDiff';

const DB_NAME = 'serpa-manifest-cache';
const STORE_NAME = 'manifest_snapshot';
// Bumpata da 1 a 2 il 2026-07-09: alcuni browser di test avevano già questo DB a versione 2 da
// un'iterazione precedente della sessione, causando "requested version (1) is less than the
// existing version (2)" al prossimo openDB. L'upgrade resta additivo (create-if-missing), quindi
// il bump è sicuro anche per chi ha ancora la v1.
const DB_VERSION = 2;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'mob_usc_id' });
        }
      }
    });
  }
  return dbPromise;
}

// Legge l'intero snapshot noto — usato come "previous" per il diff. Cache vuota (primo
// caricamento, o dopo un clearManifestCache) restituisce [], diffManifestTree tratta
// correttamente questo caso: tutto risulta "added" (vedi manifestDiff.test.ts).
export async function getCachedManifestEntries(): Promise<ManifestEntry[]> {
  const db = await getDb();
  return db.getAll(STORE_NAME);
}

// Confronta lo snapshot fresco (appena scaricato da /api/uscite/manifest) con quello in cache,
// riscrive la cache con l'intero snapshot fresco (tranne i removed, cancellati) e restituisce il
// piano risultante: è quello che guiderà il prossimo pezzo (fetch mirato dei soli recordId segnati
// added/changed, per tabella).
//
// IMPORTANTE: si scrivono TUTTE le uscite non rimosse (added + changed + unchanged), non solo
// added/changed a livello uscita — bug reale trovato il 2026-07-09: un'uscita il cui modId
// *proprio* non cambia (es. cambia solo una sua località o un volantino, record indipendenti che
// non toccano il modId del genitore) veniva classificata "unchanged" a livello uscita e quindi MAI
// riscritta in cache. Il prossimo poll confrontava di nuovo contro l'albero VECCHIO, ritrovava la
// stessa differenza nidificata, e richiamava fetch-records da capo — all'infinito, ad ogni giro,
// anche senza nessun cambiamento reale nel frattempo. Scrivere sempre l'intero snapshot fresco
// (tranne i removed) costa qualche scrittura IndexedDB in più (poche decine di uscite/giorno, non
// migliaia — trascurabile) ma garantisce che il prossimo diff parta sempre dallo stato vero.
export async function syncManifest(freshEntries: ManifestEntry[]): Promise<ManifestDiffPlan> {
  const cached = await getCachedManifestEntries();
  const plan = diffManifestTree(cached, freshEntries);

  const db = await getDb();
  const tx = db.transaction(STORE_NAME, 'readwrite');

  await Promise.all([
    ...freshEntries.map((entry) => tx.store.put(entry)),
    ...plan.uscite.removed.map((entry) => tx.store.delete(entry.mob_usc_id)),
  ]);
  await tx.done;

  return plan;
}

// Svuota completamente la cache — da usare al logout o se la cache risulta corrotta/da scartare
// (es. cambio azienda con impersonificazione, dove mob_usc_id potrebbero collidere tra aziende).
export async function clearManifestCache(): Promise<void> {
  const db = await getDb();
  await db.clear(STORE_NAME);
}
